import { defineStore } from "pinia";
import {
  ExternalCostsApi,
  TrainingPlanDetailsItemTypeEnum,
  CostTypeEnum,
  SessionDetailsApi,
  UpdateExternalCostDtoCostTypeEnum,
  ItemType as SdkItemType,
} from "@simbelapp/order-sdk";
import { useSessionDetailsStore } from "../sessions/session-details-store";
import { useSessionCostsStore } from "../sessions/session-costs.store";
import { useFeedback } from "~/composables/feedback/useFeedback";
import type { IExternalCostStore } from "~/utils/interfaces/external-costs-interfaces";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { ItemType, MicroserviceEnum } from "~/utils/enums/common.enums";
import { usePagination } from "~/composables/paginations/usePagination";
import { PaginationKeysEnum } from "~/utils/enums/pagination-keys.enums";
import { buildAllParticipantsOption } from "~/composables/external-cost/useExternalCost";
/**
 * External Costs Store
 * Manages all types of external costs for sessions and projects:
 * - Salary costs for attendees
 * - Internal trainer costs
 * - Other costs (miscellaneous expenses)
 */
export const useExternalCostsStore = defineStore("externalCostsStore", {
  state: (): IExternalCostStore => {
    return {
      // Edit mode flags
      isEditSalary: false,
      isEditOther: false,
      isCreateOtherCost: false,
      isEditInternalTrainerCost: false,

      // Other costs data
      otherCosts: null,
      otherCostsToAdd: [],
      otherCostsToDelete: [],
      otherCostsErrors: new Map(),
      otherCostsToAddErrors: [],

      // Salary costs data
      salariesCosts: null,

      // Internal trainer costs data
      internalTrainerCosts: [],
    };
  },
  getters: {
    /**
     * Calculate total cost for internal trainers
     * Formula: duration (hours) × price per hour
     */
    totalCostInternalTrainer: (state): number =>
      state.internalTrainerCosts.reduce(
        (acc, data) => acc + Number(data.trainingDurationHours) * (Number(data.priceExclTax) || 0),
        0,
      ),
  },
  actions: {
    /**
     * Initialize store - reset all state to defaults
     */
    init() {
      this.$reset();
    },
    /**
     * Initialize the creation of a new other cost
     * Sets up the first empty cost entry with default values
     */
    initCreateOtherCost() {
      const sessionDetailsStore = useSessionDetailsStore();
      const { sessionDetails } = sessionDetailsStore;
      const itemType =
        sessionDetails?.itemType === TrainingPlanDetailsItemTypeEnum.Session
          ? SdkItemType.Session
          : SdkItemType.ProjectUser;
      this.otherCostsToAdd = [];
      this.otherCostsToAddErrors = [];
      this.otherCostsToAdd.push({
        priceExclTax: 0,
        vat: 0,
        costType: CostTypeEnum.Other,
        itemId: "",
        attendee: null,
        itemType,
      });
      this.otherCostsToAddErrors.push({
        type: false,
        for: false,
        cost: false,
      });
      this.isCreateOtherCost = true;
    },

    /**
     * Add another cost entry to the creation list
     * Allows creating multiple costs at once
     */
    addOtherCost() {
      const sessionDetailsStore = useSessionDetailsStore();
      const { sessionDetails } = sessionDetailsStore;
      const itemType =
        sessionDetails?.itemType === TrainingPlanDetailsItemTypeEnum.Session
          ? SdkItemType.Session
          : SdkItemType.ProjectUser;
      this.otherCostsToAdd.push({
        priceExclTax: 0,
        vat: 0,
        costType: CostTypeEnum.Other,
        itemId: "",
        attendee: null,
        itemType,
      });
      this.otherCostsToAddErrors.push({
        type: false,
        for: false,
        cost: false,
      });
    },
    /**
     * Cancel salary costs edit mode
     * Refreshes data from server and exits edit mode
     */
    async cancelSalaryEdit() {
      const paginate = usePagination(PaginationKeysEnum.SALARIES_COSTS_PAGINATION);
      const paginationOptions = paginate.getFindOptions(paginate.paginateOptions.value);
      await this.getSalariesCosts(paginationOptions);

      this.isEditSalary = false;
    },

    /**
     * Cancel other costs edit/create mode
     * Refreshes data, clears pending changes and errors
     */
    async cancelOtherEdit() {
      const paginate = usePagination(PaginationKeysEnum.OTHER_COSTS_PAGINATION);
      const paginationOptions = paginate.getFindOptions(paginate.paginateOptions.value);
      await this.getOtherCosts(paginationOptions);
      this.isEditOther = false;
      this.isCreateOtherCost = false;
      this.resetOtherCosts();
    },
    /**
     * Reset other costs data
     * Clears all other costs data and errors
     */
    resetOtherCosts() {
      this.otherCostsToAdd = [];
      this.otherCostsToAddErrors = [];
      this.otherCostsToDelete = [];
      this.otherCostsErrors.clear();
    },

    /**
     * Cancel internal trainer costs edit mode
     * Refreshes data from server and exits edit mode
     */
    async cancelInternalTrainerEdit() {
      await this.getInternalTrainerCosts();

      this.isEditInternalTrainerCost = false;
    },
    /**
     * Fetch other costs for session/project attendees
     * @param paginationOptions - Pagination parameters for the query
     */
    async getOtherCosts(paginationOptions: any) {
      let transformQuery = {};

      // Transform pagination options to camelCase or use noLimit
      if (paginationOptions) {
        const paginateQuery = new URLSearchParams(paginationOptions);
        transformQuery = Object.fromEntries(paginateQuery.entries());
      } else {
        transformQuery = {
          noLimit: true,
        };
      }

      const feedback = useFeedback();
      const apiInstanceSessionDetails = await useSDKApi(MicroserviceEnum.ORDERS, SessionDetailsApi);
      const sessionDetailsStore = useSessionDetailsStore();
      const { sessionDetails } = sessionDetailsStore;

      try {
        const otherCosts = await apiInstanceSessionDetails.getAttendeesWithOtherCosts({
          itemId: sessionDetails?.id as string,
          itemType: sessionDetails?.itemType as ItemType,
          ...transformQuery,
        });

        this.otherCosts = otherCosts;
      } catch {
        feedback.error(`Une erreur est survenue`, "small");
      }
    },
    /**
     * `servicesAPI` callback for the attendee dropdown in the cost forms:
     * fetches via `getBasicAttendees` and prepends the "Tous les participants"
     * sentinel on the first page only.
     */
    async fetchAttendeesWithAllOption(params: any, opts: { itemId?: string | null; itemType?: any }) {
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, SessionDetailsApi);
      const data = await apiInstance.getBasicAttendees({
        lazyLoad: true,
        enableRolesFilters: false,
        ...params,
        itemId: opts.itemId,
        itemType: opts.itemType,
      });
      if (!data) return;
      if (params?.offset) return data;
      return {
        ...data,
        data: [buildAllParticipantsOption(opts.itemId ?? ""), ...(data.data || [])],
        meta: {
          ...data.meta,
          count: (data.meta?.count || 0) + 1,
          totalCount: typeof data.meta?.totalCount === "number" ? data.meta.totalCount + 1 : data.meta?.totalCount,
        },
      };
    },
    /**
     * Create new other costs
     * Validates all costs before creating them in batch
     */
    async createOtherCost() {
      const feedback = useFeedback();

      // Validate before creating
      if (this.validator()) {
        feedback.error(`Veuillez remplir tous les champs obligatoires`, "small");
        return;
      }

      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, ExternalCostsApi);
      const { sessionDetails } = useSessionDetailsStore();
      const paginate = usePagination(PaginationKeysEnum.OTHER_COSTS_PAGINATION);
      const paginationOptions = paginate.getFindOptions(paginate.paginateOptions.value);

      try {
        const otherCosts = await apiInstance.createExternalCost({
          batchCreateExternalCostDto: {
            createExternalCostsDto: this.otherCostsToAdd.map((other) => {
              // Check if cost is for all attendees (session/project level)
              const isForAll = other.attendee?.id && sessionDetails?.id === other.attendee?.id;

              // Determine correct item type based on scope
              let itemTypeSelected;

              if (isForAll && sessionDetails.itemType === TrainingPlanDetailsItemTypeEnum.Project) {
                itemTypeSelected = TrainingPlanDetailsItemTypeEnum.Project;
              } else if (isForAll && sessionDetails.itemType === TrainingPlanDetailsItemTypeEnum.Session) {
                itemTypeSelected = TrainingPlanDetailsItemTypeEnum.Session;
              } else if (!isForAll && other?.itemType === SdkItemType.ProjectUser) {
                itemTypeSelected = TrainingPlanDetailsItemTypeEnum.ProjectUser;
              } else {
                itemTypeSelected = TrainingPlanDetailsItemTypeEnum.Booking;
              }

              return {
                priceExclTax: other.priceExclTax,
                vat: other.vat,
                costType: other.costType,
                itemId: other.attendee?.id as string,
                itemType: itemTypeSelected,
              };
            }),
          },
        });

        if (otherCosts) {
          this.otherCostsToAdd = [];
          this.otherCostsToAddErrors = [];
          feedback.success(`Vos modifications ont été sauvegardées`, "small");
          this.getOtherCosts(paginationOptions);
          this.isCreateOtherCost = false;
          useSessionCostsStore().refreshIndicators();
        }
      } catch {
        feedback.error(`Une erreur est survenue`, "small");
      }
    },
    /**
     * Update existing other costs
     * Validates data, updates costs, and deletes marked items
     * Exits edit mode after successful update
     */
    async updateExternalCosts() {
      const feedback = useFeedback();
      const sessionDetailsStore = useSessionDetailsStore();
      const { sessionDetails } = sessionDetailsStore;

      if (!this.validator()) {
        try {
          const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, ExternalCostsApi);
          const paginate = usePagination(PaginationKeysEnum.OTHER_COSTS_PAGINATION);
          const paginationOptions = paginate.getFindOptions(paginate.paginateOptions.value);

          let updatedOtherCosts;
          if (this.otherCosts?.data && this.otherCosts.data?.length) {
            updatedOtherCosts = await apiInstance.updateExternalCosts({
              batchUpdateExternalCostDto: {
                updateExternalCostsDto: this.otherCosts.data.map((other) => {
                  // Check if cost is for all attendees (session/project level)
                  const isForAll = other.id && sessionDetails?.id === other.id;

                  // Determine correct item type based on scope
                  let itemTypeSelected;

                  if (isForAll && sessionDetails.itemType === TrainingPlanDetailsItemTypeEnum.Project) {
                    itemTypeSelected = TrainingPlanDetailsItemTypeEnum.Project;
                  } else if (isForAll && sessionDetails.itemType === TrainingPlanDetailsItemTypeEnum.Session) {
                    itemTypeSelected = TrainingPlanDetailsItemTypeEnum.Session;
                  } else if (!isForAll && sessionDetails?.itemType === TrainingPlanDetailsItemTypeEnum.Project) {
                    itemTypeSelected = TrainingPlanDetailsItemTypeEnum.ProjectUser;
                  } else {
                    itemTypeSelected = TrainingPlanDetailsItemTypeEnum.Booking;
                  }

                  return {
                    priceExclTax: other.priceExclTax,
                    externalCostId: other.externalCostId,
                    costType: other.costType as UpdateExternalCostDtoCostTypeEnum,
                    itemType: itemTypeSelected,
                    itemId: other.id,
                  };
                }),
              },
            });
          }

          // Delete costs marked for deletion
          if (this.otherCostsToDelete.length > 0) {
            await this.deleteOtherCost();
          }

          feedback.success(`Vos modifications ont été sauvegardées`, "small");
          this.isEditOther = false;
          this.resetOtherCosts();
          await this.getOtherCosts(paginationOptions);
          useSessionCostsStore().refreshIndicators();
        } catch {
          feedback.error(`Une erreur est survenue`, "small");
        }
      } else {
        feedback.error(`Veuillez remplir tous les champs obligatoires`, "small");
      }
    },
    /**
     * Delete other costs marked for deletion
     * Uses IDs from otherCostsToDelete array
     */
    async deleteOtherCost() {
      const feedback = useFeedback();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, ExternalCostsApi);

      try {
        await apiInstance.deleteExternalCosts({ deleteExternalCostDto: { externalCostIds: this.otherCostsToDelete } });
      } catch {
        feedback.error(`Une erreur est survenue`, "small");
      }
    },

    /**
     * Fetch salary costs for session/project attendees
     * @param paginationOptions - Pagination parameters for the query
     */
    async getSalariesCosts(paginationOptions: any) {
      let transformQuery = {};

      // Transform pagination options to camelCase or use noLimit
      if (paginationOptions) {
        const paginateQuery = new URLSearchParams(paginationOptions);
        transformQuery = Object.fromEntries(paginateQuery.entries());
      } else {
        transformQuery = {
          noLimit: true,
        };
      }

      const feedback = useFeedback();
      const apiInstanceSessionDetails = await useSDKApi(MicroserviceEnum.ORDERS, SessionDetailsApi);
      const sessionDetailsStore = useSessionDetailsStore();
      const { sessionDetails } = sessionDetailsStore;

      try {
        const salaries = await apiInstanceSessionDetails.getAttendeesWithSalaries({
          itemId: sessionDetails?.id as string,
          itemType: sessionDetails?.itemType as ItemType,
          ...transformQuery,
        });

        this.salariesCosts = salaries;
      } catch {
        feedback.error(`Une erreur est survenue`, "small");
      }
    },
    /**
     * Save salary costs for each attendee
     * Handles both creation of new costs and updates to existing ones
     * Exits edit mode after successful save
     */
    async saveSalaryCosts() {
      const feedback = useFeedback();
      const sessionDetailsStore = useSessionDetailsStore();
      const { sessionDetails } = sessionDetailsStore;

      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, ExternalCostsApi);
        const paginate = usePagination(PaginationKeysEnum.SALARIES_COSTS_PAGINATION);
        const paginationOptions = paginate.getFindOptions(paginate.paginateOptions.value);

        if (this.salariesCosts?.data) {
          // Separate costs to create and those to update
          const salariesToCreate = this.salariesCosts.data.filter(
            (salary) => !salary.externalCostId && salary.priceExclTax && salary.priceExclTax > 0,
          );
          const salariesToUpdate = this.salariesCosts.data.filter((salary) => salary.externalCostId);

          // Create new salary costs
          if (salariesToCreate.length > 0) {
            await apiInstance.createExternalCost({
              batchCreateExternalCostDto: {
                createExternalCostsDto: salariesToCreate.map((salary) => {
                  return {
                    itemId: salary.id,
                    itemType:
                      sessionDetails?.itemType === TrainingPlanDetailsItemTypeEnum.Project
                        ? TrainingPlanDetailsItemTypeEnum.ProjectUser
                        : TrainingPlanDetailsItemTypeEnum.Booking,
                    priceExclTax: salary.priceExclTax,
                    costType: CostTypeEnum.Salary,
                  };
                }),
              },
            });
          }

          // Update existing costs
          if (salariesToUpdate.length > 0) {
            await apiInstance.updateExternalCosts({
              batchUpdateExternalCostDto: {
                updateExternalCostsDto: salariesToUpdate.map((salary) => {
                  return {
                    externalCostId: salary.externalCostId,
                    itemId: salary.id,
                    itemType:
                      sessionDetails?.itemType === TrainingPlanDetailsItemTypeEnum.Project
                        ? TrainingPlanDetailsItemTypeEnum.ProjectUser
                        : TrainingPlanDetailsItemTypeEnum.Booking,
                    priceExclTax: salary.priceExclTax,
                  };
                }),
              },
            });
          }

          feedback.success(`Vos modifications ont été sauvegardées`, "small");
          await this.getSalariesCosts(paginationOptions);
          this.isEditSalary = false;
          useSessionCostsStore().refreshIndicators();
        }
      } catch {
        feedback.error(`Une erreur est survenue`, "small");
      }
    },
    /**
     * Fetch internal trainer costs for timeslots
     * Retrieves salary information for internal trainers
     */
    async getInternalTrainerCosts() {
      const feedback = useFeedback();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, ExternalCostsApi);
      const { sessionDetails } = useSessionDetailsStore();

      try {
        if (sessionDetails?.itemType && sessionDetails?.id) {
          const data = await apiInstance.getTimeslotsTrainersSalaries({
            itemId: sessionDetails?.id,
            itemType: sessionDetails?.itemType,
          });
          if (data) {
            this.internalTrainerCosts = data;
          }
        }
      } catch {
        feedback.error(`Une erreur est survenue`, "small");
      }
    },

    /**
     * Save internal trainer costs
     * Updates salary information for each internal trainer
     * Exits edit mode after successful save
     */
    async saveInternalTrainerCosts() {
      const feedback = useFeedback();
      const sessionDetailsStore = useSessionDetailsStore();
      const { sessionDetails } = sessionDetailsStore;

      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, ExternalCostsApi);

        // Map internal trainer costs to update format
        const internalTrainers = this.internalTrainerCosts.map((internalTrainer) => {
          return {
            externalCostId: internalTrainer.externalCostId || "",
            itemId: internalTrainer.itemId as string,
            itemType: internalTrainer?.itemType as TrainingPlanDetailsItemTypeEnum,
            priceExclTax: internalTrainer.priceExclTax,
            costType: CostTypeEnum.TrainerSalary,
          };
        });

        let sendInternalTrainerCosts;
        if (sessionDetails?.itemType && sessionDetails?.id) {
          sendInternalTrainerCosts = await apiInstance.updateExternalCosts({
            batchUpdateExternalCostDto: {
              updateExternalCostsDto: internalTrainers,
            },
          });
        }

        if (sendInternalTrainerCosts) {
          feedback.success(`Vos modifications ont été sauvegardées`, "small");
          this.getInternalTrainerCosts();
          this.isEditInternalTrainerCost = false;
          useSessionCostsStore().refreshIndicators();
        }
      } catch {
        feedback.error(`Une erreur est survenue`, "small");
      }
    },
    /**
     * Validate other costs data
     * Checks for required fields and valid values in both edit and create modes
     * @returns {boolean} - Returns true if there are validation errors, false otherwise
     */
    validator() {
      let hasErrors = false;

      // Validate existing costs in edit mode
      if (this.isEditOther && this.otherCosts?.data) {
        this.otherCostsErrors.clear();

        this.otherCosts.data.forEach((data) => {
          const errors = {
            type: false,
            for: false,
            cost: false,
          };

          // Validate cost value
          if (!Number.isFinite(data.priceExclTax) || data.priceExclTax === null) {
            errors.cost = true;
            hasErrors = true;
          }

          // Validate cost type
          if (!data.costType) {
            errors.type = true;
            hasErrors = true;
          }

          // Validate attendee (check that there is an id)
          // For "All attendees", userId is empty but id exists
          if (!data.id) {
            errors.for = true;
            hasErrors = true;
          }

          // Store errors with external cost ID as key
          if (errors.type || errors.for || errors.cost) {
            this.otherCostsErrors.set(data.externalCostId, errors);
          }
        });
      }

      // Validate new costs in create mode
      if (this.isCreateOtherCost && this.otherCostsToAdd.length > 0) {
        this.otherCostsToAdd.forEach((data, index) => {
          const errors = {
            type: false,
            for: false,
            cost: false,
          };

          // Validate cost value (must be a positive number)
          if (!Number.isFinite(data.priceExclTax) || data.priceExclTax === null || data.priceExclTax === 0) {
            errors.cost = true;
            hasErrors = true;
          }

          // Validate cost type
          if (!data.costType) {
            errors.type = true;
            hasErrors = true;
          }

          // Validate attendee
          if (!data.attendee || !data.attendee.id) {
            errors.for = true;
            hasErrors = true;
          }

          this.otherCostsToAddErrors[index] = errors;
        });
      }

      return hasErrors;
    },
  },
});
